import { useState, useEffect, useCallback } from "react";
import {
  HiOutlineUserGroup, HiOutlineArrowLeft, HiOutlineCalendarDays,
  HiOutlineMapPin, HiOutlineCheckCircle, HiOutlineXMark,
  HiOutlinePlus, HiOutlineTrash, HiOutlineEnvelope, HiOutlinePhone, HiOutlineFlag,
  HiOutlineExclamationTriangle, HiOutlineSparkles, HiOutlineChatBubbleLeftRight,
} from "react-icons/hi2";
import AdminLayout from "./AdminLayout";
import { cohortsAPI, retreatBlocksAPI } from "../services/api";
import { useCurrentAdmin, hasPermission } from "./permissions";
import CohortManager from "./CohortManager";
import toast from "react-hot-toast";
import "./AdminCohorts.css";

const fmt   = (d) => d ? new Date(d).toLocaleDateString("en-US", { year:"numeric", month:"short", day:"numeric" }) : "—";
const fmtDT = (d) => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";

const ACTIVITY_META = {
  note:      { icon: HiOutlineChatBubbleLeftRight, color:"#6b6258", bg:"rgba(107,98,88,0.1)",  label:"Note" },
  milestone: { icon: HiOutlineSparkles,             color:"#16a34a", bg:"rgba(22,163,74,0.1)",  label:"Milestone" },
  incident:  { icon: HiOutlineExclamationTriangle,  color:"#dc2626", bg:"rgba(220,38,38,0.1)",  label:"Incident" },
  update:    { icon: HiOutlineFlag,                 color:"#2563eb", bg:"rgba(37,99,235,0.1)",  label:"Update" },
};

/* ── Log activity modal ── */
function ActivityModal({ onClose, onSaved, blockId }) {
  const [type,  setType]  = useState("note");
  const [title, setTitle] = useState("");
  const [desc,  setDesc]  = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) { toast.error("A title is required."); return; }
    setSaving(true);
    try {
      await cohortsAPI.addActivity(blockId, { type, title: title.trim(), description: desc.trim() || null });
      toast.success("Activity logged.");
      onSaved();
    } catch (err) { toast.error(err.message || "Failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="aap__contact-overlay" onClick={onClose}>
      <div className="aap__contact-modal" onClick={e=>e.stopPropagation()} style={{maxWidth:"440px"}}>
        <div className="aap__contact-head">
          <h3>Log Cohort Activity</h3>
          <button type="button" onClick={onClose}><HiOutlineXMark/></button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:"0.75rem"}}>
          <div>
            <label style={{fontSize:"0.78rem",fontWeight:700,color:"#1b1b1b",display:"block",marginBottom:"0.3rem"}}>Type</label>
            <div style={{display:"flex",gap:"0.4rem",flexWrap:"wrap"}}>
              {Object.entries(ACTIVITY_META).map(([k,v]) => (
                <button key={k} type="button"
                  onClick={()=>setType(k)}
                  style={{
                    display:"flex",alignItems:"center",gap:"0.3rem",
                    padding:"0.35rem 0.7rem",borderRadius:"99px",fontSize:"0.75rem",fontWeight:600,
                    border: type===k ? `1.5px solid ${v.color}` : "1.5px solid #e8e3da",
                    background: type===k ? v.bg : "#fff", color: type===k ? v.color : "#6b6258",
                    cursor:"pointer",
                  }}>
                  <v.icon style={{fontSize:"0.85rem"}}/> {v.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={{fontSize:"0.78rem",fontWeight:700,color:"#1b1b1b",display:"block",marginBottom:"0.3rem"}}>Title</label>
            <input className="arb__form-input" value={title} onChange={e=>setTitle(e.target.value)}
              placeholder="e.g. Group arrived at the maloca"/>
          </div>
          <div>
            <label style={{fontSize:"0.78rem",fontWeight:700,color:"#1b1b1b",display:"block",marginBottom:"0.3rem"}}>Details (optional)</label>
            <textarea className="arb__form-input arb__form-textarea" rows={3} value={desc}
              onChange={e=>setDesc(e.target.value)}/>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:"0.5rem"}}>
            <button type="button" className="a-btn a-btn--outline" onClick={onClose}>Cancel</button>
            <button type="button" className="a-btn a-btn--rust" disabled={saving} onClick={handleSave}>
              {saving?"Saving…":"Log Activity"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Broadcast to cohort modal ── */
function CohortBroadcastModal({ blockId, attendeeCount, onClose, onSent }) {
  const [subject, setSubject] = useState("");
  const [heading, setHeading] = useState("");
  const [body,    setBody]    = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!subject.trim() || !heading.trim() || !body.trim()) { toast.error("Subject, heading, and message are required."); return; }
    setSending(true);
    try {
      const res = await cohortsAPI.broadcast(blockId, { subject, heading, body });
      toast.success(res?.message || "Sent.");
      onSent();
    } catch (err) { toast.error(err.message || "Failed."); }
    finally { setSending(false); }
  };

  return (
    <div className="aap__contact-overlay" onClick={onClose}>
      <div className="aap__contact-modal" onClick={e=>e.stopPropagation()} style={{maxWidth:"460px"}}>
        <div className="aap__contact-head">
          <h3>Email This Cohort</h3>
          <button type="button" onClick={onClose}><HiOutlineXMark/></button>
        </div>
        <p style={{fontSize:"0.8rem",color:"#6b6258",marginBottom:"1rem"}}>
          This will be sent to all {attendeeCount} confirmed attendee{attendeeCount!==1?"s":""} in this cohort.
        </p>
        <div style={{display:"flex",flexDirection:"column",gap:"0.75rem"}}>
          <div>
            <label style={{fontSize:"0.78rem",fontWeight:700,color:"#1b1b1b",display:"block",marginBottom:"0.3rem"}}>Email Subject</label>
            <input className="arb__form-input" value={subject} onChange={e=>setSubject(e.target.value)}/>
          </div>
          <div>
            <label style={{fontSize:"0.78rem",fontWeight:700,color:"#1b1b1b",display:"block",marginBottom:"0.3rem"}}>Heading</label>
            <input className="arb__form-input" value={heading} onChange={e=>setHeading(e.target.value)}/>
          </div>
          <div>
            <label style={{fontSize:"0.78rem",fontWeight:700,color:"#1b1b1b",display:"block",marginBottom:"0.3rem"}}>Message</label>
            <textarea className="arb__form-input arb__form-textarea" rows={5} value={body} onChange={e=>setBody(e.target.value)}/>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:"0.5rem"}}>
            <button type="button" className="a-btn a-btn--outline" onClick={onClose}>Cancel</button>
            <button type="button" className="a-btn a-btn--rust" disabled={sending} onClick={handleSend}>
              <HiOutlineEnvelope/> {sending?"Sending…":"Send to Cohort"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Cohort detail view ── */
function CohortDetail({ blockId, onBack }) {
  const { admin, loading: permLoading } = useCurrentAdmin();
  const canManage = permLoading ? true : hasPermission(admin, "cohorts.manage");

  const [cohort,  setCohort]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [showActivity,  setShowActivity]  = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [completing, setCompleting] = useState(false);

  const fetchCohort = useCallback(() => {
    setLoading(true);
    cohortsAPI.getOne(blockId)
      .then(res => setCohort(res?.data || null))
      .catch(() => toast.error("Failed to load cohort."))
      .finally(() => setLoading(false));
  }, [blockId]);

  useEffect(() => { fetchCohort(); }, [fetchCohort]);

  const handleCheckIn = async (applicantId) => {
    setBusyId(applicantId);
    try { await cohortsAPI.toggleCheckIn(blockId, applicantId); fetchCohort(); }
    catch (err) { toast.error(err.message || "Failed."); }
    finally { setBusyId(null); }
  };

  const handleRemoveActivity = async (activityId) => {
    try { await cohortsAPI.removeActivity(blockId, activityId); fetchCohort(); }
    catch (err) { toast.error(err.message || "Failed."); }
  };

  const handleCompleteRetreat = () => {
    toast((t) => (
      <div style={{display:"flex",flexDirection:"column",gap:"0.5rem"}}>
        <p style={{margin:0,fontWeight:700}}>Mark this retreat completed?</p>
        <p style={{margin:0,fontSize:"0.78rem",color:"#6b6258",lineHeight:1.5}}>
          All {cohort?.attendee_count || 0} confirmed attendee{cohort?.attendee_count!==1?"s":""} will become
          eligible for a future retreat again — no new application needed. This can't be undone.
        </p>
        <div style={{display:"flex",gap:"0.5rem"}}>
          <button onClick={async ()=>{
            toast.dismiss(t.id); setCompleting(true);
            try {
              const res = await retreatBlocksAPI.complete(blockId);
              toast.success(res?.message || "Retreat marked completed.");
              fetchCohort();
            } catch (err) { toast.error(err.message || "Failed."); }
            finally { setCompleting(false); }
          }} style={{background:"#16a34a",color:"#fff",border:"none",borderRadius:"5px",padding:"0.35rem 0.75rem",cursor:"pointer",fontWeight:700,fontSize:"0.78rem"}}>
            Mark Completed
          </button>
          <button onClick={()=>toast.dismiss(t.id)} style={{background:"#f3f4f0",border:"none",borderRadius:"5px",padding:"0.35rem 0.75rem",cursor:"pointer",fontSize:"0.78rem"}}>
            Cancel
          </button>
        </div>
      </div>
    ), { duration:Infinity, style:{maxWidth:"340px"} });
  };

  if (loading) return <div className="arb__loading"><div className="acr__spinner"/></div>;
  if (!cohort) return null;

  return (
    <div className="arb__detail">
      <button type="button" className="arb__back-btn" onClick={onBack}>
        <HiOutlineArrowLeft/> All Cohorts
      </button>

      <div className="acard arb__detail-header">
        <div className="arb__detail-top">
          <div>
            <h2 className="arb__detail-title">{cohort.name}</h2>
            <p className="arb__detail-dates"><HiOutlineCalendarDays/> {fmt(cohort.start_date)} – {fmt(cohort.end_date)}</p>
            {cohort.location && <p className="arb__detail-location"><HiOutlineMapPin/> {cohort.location}</p>}
          </div>
          {canManage && (
            <div style={{display:"flex",gap:"0.5rem",flexWrap:"wrap"}}>
              <button type="button" className="a-btn a-btn--outline" onClick={()=>setShowBroadcast(true)}>
                <HiOutlineEnvelope/> Email Cohort
              </button>
              {cohort.status !== "completed" && cohort.status !== "cancelled" && (
                <button type="button" className="a-btn a-btn--rust" disabled={completing} onClick={handleCompleteRetreat}>
                  <HiOutlineCheckCircle/> {completing ? "Completing…" : "Mark Retreat Completed"}
                </button>
              )}
              {cohort.status === "completed" && (
                <span className="ach__completed-badge"><HiOutlineCheckCircle/> Completed</span>
              )}
            </div>
          )}
        </div>

        {/* Summary stats */}
        <div className="ach__stats-row">
          <div className="ach__stat">
            <p className="ach__stat-num">{cohort.attendee_count}</p>
            <p className="ach__stat-label">Attendees</p>
          </div>
          <div className="ach__stat">
            <p className="ach__stat-num">{cohort.checked_in_count}</p>
            <p className="ach__stat-label">Checked In</p>
          </div>
          <div className="ach__stat">
            <p className="ach__stat-num">{Object.keys(cohort.category_breakdown).length}</p>
            <p className="ach__stat-label">Categories</p>
          </div>
        </div>

        <div className="ach__category-chips">
          {Object.entries(cohort.category_breakdown).map(([cat,count]) => (
            <span key={cat} className="ach__cat-chip">{cat}: {count}</span>
          ))}
        </div>
      </div>

      {/* Roster */}
      <div className="acard arb__applicants-card">
        <p className="p-section-title" style={{padding:"1.1rem 1.25rem 0"}}>Roster</p>
        <table className="aap__table" style={{width:"100%"}}>
          <thead>
            <tr><th>Name</th><th>Room Type</th><th>Contact</th><th>Emergency Contact</th><th>Check-In</th></tr>
          </thead>
          <tbody>
            {cohort.roster.map(r => (
              <tr key={r.id}>
                <td style={{fontWeight:600}}>{r.first_name} {r.last_name}</td>
                <td>{r.room_type_name ? `${r.room_type_name}${r.is_free?" (Free)":""}` : "—"}</td>
                <td style={{fontSize:"0.8rem",color:"#6b6258"}}>
                  <div><HiOutlineEnvelope style={{verticalAlign:"-2px",marginRight:"0.3rem"}}/>{r.email}</div>
                  {r.phone && <div><HiOutlinePhone style={{verticalAlign:"-2px",marginRight:"0.3rem"}}/>{r.phone}</div>}
                </td>
                <td style={{fontSize:"0.78rem",color:"#6b6258"}}>
                  {r.ec_name ? `${r.ec_name}${r.ec_relationship?` (${r.ec_relationship})`:""}${r.ec_phone?` · ${r.ec_phone}`:""}` : "—"}
                </td>
                <td>
                  {canManage ? (
                    <button type="button"
                      className={`ach__checkin-btn ${r.checked_in_at?"ach__checkin-btn--done":""}`}
                      disabled={busyId===r.id}
                      onClick={()=>handleCheckIn(r.id)}>
                      <HiOutlineCheckCircle/> {r.checked_in_at ? fmtDT(r.checked_in_at) : "Check In"}
                    </button>
                  ) : (
                    r.checked_in_at
                      ? <span style={{color:"#16a34a",fontSize:"0.78rem",fontWeight:700}}><HiOutlineCheckCircle/> {fmtDT(r.checked_in_at)}</span>
                      : <span style={{color:"#9ca3af",fontSize:"0.78rem"}}>Not yet</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Ceremony cohorts — the retreat's confirmed attendees split into the
          smaller groups that actually go through ceremony together. */}
      <div className="acard" style={{ padding: "1.25rem" }}>
        <CohortManager blockId={blockId} />
      </div>

      {/* Activity log */}
      <div className="acard aq__section">
        <div className="aq__section-head">
          <p className="p-section-title" style={{margin:0}}>Activity Log</p>
          {canManage && (
            <button type="button" className="a-btn a-btn--rust" onClick={()=>setShowActivity(true)}>
              <HiOutlinePlus/> Log Activity
            </button>
          )}
        </div>
        {cohort.activities.length === 0 ? (
          <p className="aap__placeholder" style={{padding:"1.5rem",textAlign:"center"}}>No activity logged yet.</p>
        ) : (
          <div className="ach__activity-list">
            {cohort.activities.map(act => {
              const meta = ACTIVITY_META[act.type] || ACTIVITY_META.note;
              return (
                <div key={act.id} className="ach__activity-row">
                  <div className="ach__activity-icon" style={{background:meta.bg,color:meta.color}}>
                    <meta.icon/>
                  </div>
                  <div className="ach__activity-body">
                    <p className="ach__activity-title">{act.title}</p>
                    {act.description && <p className="ach__activity-desc">{act.description}</p>}
                    <p className="ach__activity-meta">{act.admin_name || "Admin"} · {fmtDT(act.created_at)}</p>
                  </div>
                  {canManage && (
                    <button type="button" className="a-btn a-btn--ghost" style={{color:"#dc2626"}}
                      onClick={()=>handleRemoveActivity(act.id)}>
                      <HiOutlineTrash/>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showActivity && (
        <ActivityModal blockId={blockId} onClose={()=>setShowActivity(false)}
          onSaved={()=>{ setShowActivity(false); fetchCohort(); }}/>
      )}
      {showBroadcast && (
        <CohortBroadcastModal blockId={blockId} attendeeCount={cohort.attendee_count}
          onClose={()=>setShowBroadcast(false)}
          onSent={()=>setShowBroadcast(false)}/>
      )}
    </div>
  );
}

/* ── Main page — list of cohorts ── */
export default function AdminCohorts() {
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const fetchCohorts = useCallback(() => {
    setLoading(true);
    cohortsAPI.list()
      .then(res => setCohorts(res?.data || []))
      .catch(() => toast.error("Failed to load cohorts."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchCohorts(); }, [fetchCohorts]);

  if (viewing) {
    return (
      <AdminLayout pageTitle="Cohorts" pageSubtitle="" permission="cohorts.view">
        <CohortDetail blockId={viewing} onBack={()=>{ setViewing(null); fetchCohorts(); }}/>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Cohorts" pageSubtitle="Manage confirmed attendee groups for each retreat." permission="cohorts.view">
      {loading ? (
        <div className="arb__loading"><div className="acr__spinner"/></div>
      ) : cohorts.length === 0 ? (
        <div className="acard arb__empty">
          <HiOutlineUserGroup className="arb__empty-icon"/>
          <p className="arb__empty-title">No cohorts yet</p>
          <p className="arb__empty-sub">A cohort forms automatically once applicants are confirmed for a retreat block.</p>
        </div>
      ) : (
        <div className="arb__grid">
          {cohorts.map(c => {
            const pct = c.attendee_count ? Math.round((c.checked_in_count / c.attendee_count) * 100) : 0;
            return (
              <div key={c.id} className="acard arb__card" onClick={()=>setViewing(c.id)}>
                <p className="arb__card-title">{c.name}</p>
                <p className="arb__card-dates"><HiOutlineCalendarDays/> {fmt(c.start_date)} – {fmt(c.end_date)}</p>
                {c.location && <p className="arb__card-location"><HiOutlineMapPin/> {c.location}</p>}
                <div className="arb__card-capacity">
                  <div className="arb__capacity-row">
                    <span className="arb__capacity-label"><HiOutlineUserGroup/> {c.attendee_count} attendees</span>
                    <span className="arb__capacity-count">{c.checked_in_count} checked in</span>
                  </div>
                  <div className="arb__capacity-track">
                    <div className="arb__capacity-fill" style={{width:`${pct}%`, background:"#16a34a"}}/>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}
